import { Fragment, useState, type ReactElement } from 'react';
import {
    ActionIcon,
    Alert,
    Badge,
    Box,
    Button,
    Card,
    Checkbox,
    Code,
    Collapse,
    Group,
    Loader,
    Modal,
    Select,
    Stack,
    Table,
    Text,
    Textarea,
    Title,
} from '@mantine/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ProviderIconComponent from '@/shared/ProviderIconComponent';
import { LicensesPageViewModel, type SyncFailureItem, type SyncResultBanner } from './LicensesPageViewModel';
import type { LicensePool } from './LicensesApiService';

/** Synced license pools with seat health (idle seats) and sync/assign/unassign
 *  management — the main content of the Licenses page. Reads from the shared page
 *  ViewModel resolved in {@link LicensesPage}; the summary stats, Sync all action,
 *  and recommendations live in the page header and side rail. */
export default function LicenseSummaryPanel({ vm }: { vm: LicensesPageViewModel }): ReactElement {
    const assignModalPool = vm.assignModalPool();

    return (
        <Box>
            <Stack gap="lg">
                {vm.lastSyncResult && <SyncResultBannerComponent result={vm.lastSyncResult} />}

                {vm.loading ?
                    <Group>
                        <Loader size="sm" />
                        <Text>Loading pools…</Text>
                    </Group>
                : vm.loadError ?
                    <Alert color="red" title="Failed to load pools">
                        {vm.loadError}
                    </Alert>
                : vm.pools.length === 0 ?
                    <Alert color="yellow" title="No license pools yet">
                        No synced license pools in this tenant. Click <b>Sync all</b> above to fetch from your enabled provider connections.
                    </Alert>
                :   <Card withBorder padding={0}>
                        <PoolsTable vm={vm} />
                    </Card>
                }

                {vm.mutationLog.length > 0 && (
                    <Card withBorder padding="md">
                        <Title order={5} mb="xs">
                            Recent operations
                        </Title>
                        <MutationLogTable entries={vm.mutationLog} />
                    </Card>
                )}
            </Stack>

            <Modal
                opened={vm.assignModalConnectionId !== null}
                onClose={() => vm.closeAssignModal()}
                title={assignModalPool ? `Assign licenses — ${assignModalPool.providerDisplayName}` : 'Assign licenses'}
                size="lg"
                centered
            >
                <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                        User ID is vendor-specific: GitHub Copilot → GitHub login, Microsoft Copilot → UPN, Google → email, OpenAI → email (sends an
                        invite), Anthropic → email.
                    </Text>
                    <Textarea
                        label="User IDs"
                        description="One per line, or comma / semicolon separated."
                        placeholder={'user1@example.com\nuser2@example.com'}
                        autosize
                        minRows={4}
                        value={vm.assignInput}
                        onChange={(e) => vm.setAssignInput(e.currentTarget.value)}
                    />
                    {assignModalPool?.assignmentRoles && assignModalPool.assignmentRoles.length > 0 && (
                        <Select
                            label="Role"
                            value={vm.assignRole}
                            onChange={(v) => vm.setAssignRole(v ?? assignModalPool.assignmentRoles![0]!)}
                            data={assignModalPool.assignmentRoles.map((r) => ({ value: r, label: r }))}
                            allowDeselect={false}
                        />
                    )}
                    <Group justify="flex-end" mt="sm">
                        <Button variant="default" onClick={() => vm.closeAssignModal()} disabled={vm.mutating}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => void vm.assignFromModal()}
                            loading={vm.mutating}
                            disabled={vm.assignInput.trim().length === 0 || vm.mutating}
                        >
                            Assign
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Box>
    );
}

function PoolsTable({ vm }: { vm: LicensesPageViewModel }): ReactElement {
    return (
        <Table.ScrollContainer minWidth={880}>
            <Table striped highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th style={{ width: 32 }} />
                        <Table.Th>Provider</Table.Th>
                        <Table.Th style={{ width: 100 }}>Assigned</Table.Th>
                        <Table.Th style={{ width: 100 }}>Unassigned</Table.Th>
                        <Table.Th style={{ width: 100 }}>Total</Table.Th>
                        <Table.Th style={{ width: 100 }}>Idle</Table.Th>
                        <Table.Th style={{ width: 180 }}>Last Sync</Table.Th>
                        <Table.Th style={{ width: 180 }}>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {vm.pools.map((pool) => (
                        <PoolRow key={pool.providerConnectionId} pool={pool} vm={vm} />
                    ))}
                </Table.Tbody>
            </Table>
        </Table.ScrollContainer>
    );
}

function PoolRow({ pool, vm }: { pool: LicensePool; vm: LicensesPageViewModel }): ReactElement {
    const expanded = vm.selectedConnectionId === pool.providerConnectionId;
    const rowDisabled = vm.mutating || vm.syncing;
    const idle = vm.idleFor(pool.providerConnectionId);

    const onToggle = (): void => {
        vm.setSelectedConnectionId(expanded ? null : pool.providerConnectionId);
    };

    return (
        <Fragment>
            <Table.Tr>
                <Table.Td>
                    <ActionIcon variant="subtle" size="sm" onClick={onToggle} aria-label={expanded ? 'Collapse' : 'Expand'}>
                        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </ActionIcon>
                </Table.Td>
                <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                        <Box style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <ProviderIconComponent providerTypeId={pool.providerTypeId} size={20} />
                        </Box>
                        <Text fw={500}>{pool.providerDisplayName}</Text>
                    </Group>
                </Table.Td>
                <Table.Td>
                    <Text ff="monospace">{pool.assignedSeats}</Text>
                </Table.Td>
                <Table.Td>
                    <Text ff="monospace">{pool.availableSeats}</Text>
                </Table.Td>
                <Table.Td>
                    <Text ff="monospace">{pool.totalSeats}</Text>
                </Table.Td>
                <Table.Td>
                    {idle === null ?
                        <Text size="sm" c="dimmed">
                            —
                        </Text>
                    :   <Text ff="monospace" c={idle > 0 ? 'var(--bm-color-seat-idle)' : undefined}>
                            {idle}
                        </Text>
                    }
                </Table.Td>
                <Table.Td>
                    <Text size="sm" c="dimmed">
                        {pool.lastSyncedAt ? new Date(pool.lastSyncedAt).toLocaleString() : '—'}
                    </Text>
                </Table.Td>
                <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                        <Button
                            size="xs"
                            variant="default"
                            disabled={rowDisabled}
                            loading={vm.syncing && expanded}
                            onClick={() => void vm.syncPool(pool.providerConnectionId)}
                        >
                            Sync
                        </Button>
                        <Button size="xs" disabled={rowDisabled} onClick={() => vm.openAssignModal(pool.providerConnectionId)}>
                            Assign
                        </Button>
                    </Group>
                </Table.Td>
            </Table.Tr>
            {expanded && (
                <Table.Tr>
                    <Table.Td colSpan={8} style={{ background: 'var(--bm-color-surface-alt)', padding: 0 }}>
                        <ExpandedAssignmentsPanel vm={vm} pool={pool} />
                    </Table.Td>
                </Table.Tr>
            )}
        </Fragment>
    );
}

function ExpandedAssignmentsPanel({ vm, pool }: { vm: LicensesPageViewModel; pool: LicensePool }): ReactElement {
    const rowDisabled = vm.mutating || vm.syncing;
    const selectedCount = vm.selectedUserIds.size;
    const allSelected = vm.allVisibleSelected();

    return (
        <Box p="md">
            <Group justify="space-between" align="center" mb="sm">
                <Title order={6}>Assignments {vm.assignmentsTotal > 0 ? `(${vm.assignmentsTotal})` : ''}</Title>
                {selectedCount > 0 && (
                    <Button size="xs" color="red" variant="filled" disabled={rowDisabled} loading={vm.mutating} onClick={() => void vm.bulkUnassignSelected()}>
                        Unassign selected ({selectedCount})
                    </Button>
                )}
            </Group>
            {vm.assignmentsError && (
                <Alert color="red" mb="xs">
                    {vm.assignmentsError}
                </Alert>
            )}
            {vm.assignmentsLoading ?
                <Group>
                    <Loader size="sm" />
                    <Text>Loading assignments…</Text>
                </Group>
            : vm.assignments.length === 0 ?
                <Text c="dimmed" size="sm">
                    No assignments — click <b>Sync</b> on the {pool.providerDisplayName} row to populate.
                </Text>
            :   <AssignmentsTable
                    rows={vm.assignments}
                    rowActionsDisabled={rowDisabled}
                    allSelected={allSelected}
                    isSelected={(uid) => vm.isSelected(uid)}
                    onToggle={(uid) => vm.toggleSelected(uid)}
                    onToggleAll={(checked) => vm.setAllSelected(checked)}
                    onUnassign={(userId) => void vm.unassign(userId)}
                />
            }
        </Box>
    );
}

interface AssignmentsTableProps {
    rows: import('./LicensesApiService').LicenseAssignment[];
    rowActionsDisabled: boolean;
    allSelected: boolean;
    isSelected: (userId: string) => boolean;
    onToggle: (userId: string) => void;
    onToggleAll: (checked: boolean) => void;
    onUnassign: (userId: string) => void;
}

function AssignmentsTable({ rows, rowActionsDisabled, allSelected, isSelected, onToggle, onToggleAll, onUnassign }: AssignmentsTableProps): ReactElement {
    return (
        <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
                <Table.Tr>
                    <Table.Th style={{ width: 32 }}>
                        <Checkbox aria-label="Select all" checked={allSelected} onChange={(e) => onToggleAll(e.currentTarget.checked)} />
                    </Table.Th>
                    <Table.Th>User ID</Table.Th>
                    <Table.Th>SKU</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Assigned at</Table.Th>
                    <Table.Th style={{ width: 120 }}>Action</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {rows.map((a) => (
                    <Table.Tr key={`${a.providerConnectionId}-${a.userId}-${a.skuId}`}>
                        <Table.Td>
                            <Checkbox aria-label={`Select ${a.userId}`} checked={isSelected(a.userId)} onChange={() => onToggle(a.userId)} />
                        </Table.Td>
                        <Table.Td>
                            <Code>{a.userId}</Code>
                        </Table.Td>
                        <Table.Td>{a.skuId}</Table.Td>
                        <Table.Td>
                            <Badge variant="light" color={a.status === 'active' ? 'green' : 'gray'}>
                                {a.status}
                            </Badge>
                        </Table.Td>
                        <Table.Td>{new Date(a.assignedAt).toLocaleString()}</Table.Td>
                        <Table.Td>
                            <Button size="xs" color="red" variant="outline" disabled={rowActionsDisabled} onClick={() => onUnassign(a.userId)}>
                                Unassign
                            </Button>
                        </Table.Td>
                    </Table.Tr>
                ))}
            </Table.Tbody>
        </Table>
    );
}

function SyncResultBannerComponent({ result }: { result: SyncResultBanner }): ReactElement {
    const color = result.severity === 'success' ? 'green' : result.severity === 'warning' ? 'yellow' : 'red';
    return (
        <Alert color={color} title={result.headline}>
            <Stack gap="xs">
                <Text size="sm">{result.summary}</Text>
                {result.failures.length > 0 && (
                    <Stack gap={4}>
                        {result.failures.map((failure, i) => (
                            <SyncFailureRow key={i} failure={failure} />
                        ))}
                    </Stack>
                )}
            </Stack>
        </Alert>
    );
}

function SyncFailureRow({ failure }: { failure: SyncFailureItem }): ReactElement {
    const [open, setOpen] = useState(false);
    return (
        <Box>
            <Group gap="xs" align="center">
                <Text component="span" size="sm" fw={500}>
                    {failure.providerLabel}
                </Text>
                <Button variant="subtle" size="compact-xs" color="gray" px={0} onClick={() => setOpen((o) => !o)}>
                    {open ? 'Hide details' : 'Show details'}
                </Button>
            </Group>
            <Collapse in={open}>
                <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 4 }}>
                    {failure.rawError}
                </Code>
            </Collapse>
        </Box>
    );
}

function MutationLogTable({ entries }: { entries: import('./LicensesPageViewModel').MutationLogEntry[] }): ReactElement {
    return (
        <Table withTableBorder striped>
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>Time</Table.Th>
                    <Table.Th>Op</Table.Th>
                    <Table.Th>User ID</Table.Th>
                    <Table.Th>Result</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {entries.map((e, i) => (
                    <Table.Tr key={i}>
                        <Table.Td>{new Date(e.timestamp).toLocaleTimeString()}</Table.Td>
                        <Table.Td>
                            <Badge>{e.op}</Badge>
                        </Table.Td>
                        <Table.Td>
                            <Code>{e.userId}</Code>
                        </Table.Td>
                        <Table.Td>
                            <Text size="sm" c={e.ok ? 'green' : 'red'}>
                                {e.message}
                            </Text>
                        </Table.Td>
                    </Table.Tr>
                ))}
            </Table.Tbody>
        </Table>
    );
}
